import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";

const AssetsByCondition = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    series: [{
      name: 'Assets',
      data: []
    }],
    categories: [],
    colors: []
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Color mapping for each condition - matching AssetListLayer badge colors
  const getConditionColor = (condition) => {
    const colorMap = {
      'excellent': '#22C55E',  // Green (success)
      'good': '#3B82F6',       // Blue (info)
      'fair': '#F59E0B',       // Orange (warning)
      'poor': '#EF4444',       // Red (danger)
      'damaged': '#9CA3AF'     // Gray (neutral)
    };
    return colorMap[condition.toLowerCase()] || '#4189dd'; // Default to dusty blue
  };

  const fetchStatistics = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('authToken');

      if (!apiUrl || !token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/assets/statistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success && data.data.by_condition) {
        const categories = data.data.by_condition.map(item => 
          item.condition.charAt(0).toUpperCase() + item.condition.slice(1)
        );
        const series = data.data.by_condition.map(item => item.count);
        const colors = data.data.by_condition.map(item => 
          getConditionColor(item.condition)
        );
        
        setChartData({
          series: [{
            name: 'Assets',
            data: series
          }],
          categories: categories,
          colors: colors
        });
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
        borderRadius: 4,
        distributed: true,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    legend: {
      show: false
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Number of Assets',
        style: {
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: 'Inter, sans-serif',
          color: '#64748B'
        }
      },
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        }
      }
    },
    fill: {
      opacity: 1,
      colors: chartData.colors.length > 0 ? chartData.colors : ['#4189dd']
    },
    colors: chartData.colors.length > 0 ? chartData.colors : ['#4189dd'],
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " assets"
        }
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 5
    }
  };

  return (
    <div className='col-xxl-8 col-xl-6'>
      <div className='card h-100'>
        <div className='card-body p-24'>
          <h6 className='mb-20 fw-bold text-lg'>Assets by Condition</h6>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : chartData.series[0].data.length > 0 ? (
            <ReactApexChart 
              options={chartOptions} 
              series={chartData.series} 
              type="bar" 
              height={350} 
            />
          ) : (
            <div className="text-center py-5 text-secondary-light">
              No asset data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetsByCondition;

